import {
  ComplianceDocumentSortBy,
  ComplianceDocumentSortDirection,
  ComplianceDocumentStatus,
  ComplianceDocumentsListParams,
} from '../../../types/api';
import { formatDateForInput } from '../../../utils/formatters';

export interface ComplianceFiltersState {
  status: ComplianceDocumentStatus | '';
  type: string;
  expiryWindow: '' | '30' | '60' | '90';
  search: string;
  tags: string;
  sortBy: ComplianceDocumentSortBy;
  sortDir: ComplianceDocumentSortDirection;
  page: number;
  perPage: number;
}

export const DEFAULT_COMPLIANCE_FILTERS: ComplianceFiltersState = {
  status: '',
  type: '',
  expiryWindow: '',
  search: '',
  tags: '',
  sortBy: 'expiry_date',
  sortDir: 'asc',
  page: 1,
  perPage: 20,
};

export const parseTagsInput = (value: string): string[] => {
  if (!value.trim()) {
    return [];
  }

  const tags = value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

  return Array.from(new Set(tags));
};

const resolveExpiryRange = (
  expiryWindow: ComplianceFiltersState['expiryWindow'],
) => {
  if (!expiryWindow) {
    return null;
  }

  const windowDays = Number(expiryWindow);
  if (Number.isNaN(windowDays)) {
    return null;
  }

  const today = new Date();
  const toDate = new Date(today);
  toDate.setDate(today.getDate() + windowDays);

  return {
    expiry_from: formatDateForInput(today),
    expiry_to: formatDateForInput(toDate),
  };
};

export const buildComplianceListParams = (
  filters: ComplianceFiltersState,
): ComplianceDocumentsListParams => {
  const trimmedSearch = filters.search.trim();
  const parsedTags = parseTagsInput(filters.tags);
  const expiryRange = resolveExpiryRange(filters.expiryWindow);

  return {
    page: filters.page,
    per_page: filters.perPage,
    ...(filters.type ? { type: filters.type } : {}),
    ...(filters.status ? { status: [filters.status] } : {}),
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    ...(parsedTags.length > 0 ? { tags: parsedTags } : {}),
    ...(expiryRange ? expiryRange : {}),
    sort_by: filters.sortBy,
    sort_dir: filters.sortDir,
  };
};
